import { useTranslation } from "react-i18next";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import { Card, Col } from "react-bootstrap";
import { useEffect, useRef } from "react";
import { useState } from "react";
import getFile from "@serie3/mymediaplanner/Modules/Documents/Domain/getFile";
import crudApi from "@serie3/common/api/crudApi";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import { useInView } from "../../../../hooks/useInView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileVideo,
  faFileLines,
  faFile,
  faEye,
  faTrash,
  faPlus,
  faMinus,
  faDownload,
  faFolderOpen,
  faCheck,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FILE_DRAG_TYPE } from "./FolderTree";
import ToastMessages from "../../../../ToastMessages";

const THUMB_HEIGHT = 160;

const FileTypeIcon = ({ type }) => {
  const icon =
    type === "application/pdf"
      ? faFilePdf
      : type?.startsWith("video/")
      ? faFileVideo
      : type?.startsWith("text/")
      ? faFileLines
      : faFile;

  const color =
    type === "application/pdf"
      ? "#f34e4e"
      : type?.startsWith("video/")
      ? "#038edc"
      : type?.startsWith("text/")
      ? "#564ab1"
      : "#9299a8";

  return (
    <div style={{ textAlign: "center", color }}>
      <FontAwesomeIcon icon={icon} style={{ fontSize: 48 }} />
    </div>
  );
};

/** Shown when the row exists but the API has no bytes for it (file removed on the server). */
const MissingThumbnail = () => (
  <div
    title="The file is missing on the server"
    style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, height: THUMB_HEIGHT, color: "#9299a8", fontSize: 12 }}
  >
    <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 26, color: "#f7b84b" }} />
    <span>File missing</span>
  </div>
);

const Thumbnail = ({ previewFile }) => {
  // Files without a recognisable MIME type arrive with type undefined/"".
  const mime = previewFile?.type || "";
  if (!previewFile) return null;
  if (previewFile.missing && previewFile.status === 404) return <MissingThumbnail />;

  if (mime.startsWith("image/")) {
    return (
      <img
        src={previewFile.url}
        alt="preview"
        style={{ width: "100%", height: THUMB_HEIGHT, objectFit: "cover", display: "block" }}
      />
    );
  }
  if (previewFile.type === "application/pdf") {
    return (
      <iframe
        src={previewFile.url}
        title="pdf-thumb"
        style={{ width: "100%", height: THUMB_HEIGHT, border: "none", pointerEvents: "none" }}
        scrolling="no"
      />
    );
  }
  if (mime.startsWith("video/")) {
    return (
      <video
        src={previewFile.url}
        style={{ width: "100%", height: THUMB_HEIGHT, objectFit: "cover", display: "block" }}
        muted
      />
    );
  }
  return <FileTypeIcon type={previewFile.type} />;
};

const FileListGridItem = ({
  file,
  deleteFile = null,
  showFile = null,
  handleClick = null,
  handleFileFunction = null,
  showDelete = false,
  showAdd = false,
  /** opens the "move to folder" dialog for this file; also enables dragging onto the folder tree */
  moveFile = null,
  /** where the bytes come from (thumbnail, download); group views pass their member-gated route */
  binaryEndpoint = DocumentsEndpoints.show,
  /** extra buttons rendered in the action row, e.g. "copy to my files" */
  extraActions = null,
  /** batch mode: the card is a checkbox, clicking it toggles instead of previewing */
  selectable = false,
  selected = false,
  onToggleSelect = null,
  /** ids put on the drag: the whole selection when this card is part of it */
  dragIds = null,
}) => {
  const [previewFile, setPreviewFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [added, setAdded] = useState(false);
  const [connectionId, setConnectionId] = useState(false);
  const [used, setUsed] = useState(false);

  const { ref, inView } = useInView({ rootMargin: "400px", threshold: 0 });
  const didLoadRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!file?.id || !inView || didLoadRef.current) return;
    didLoadRef.current = true;
    getFile(file, setPreviewTitle, setPreviewFile, null, binaryEndpoint);
  }, [file, inView, binaryEndpoint]);

  useEffect(() => {
    didLoadRef.current = false;
  }, [file?.id]);

  const downloadFile = async () => {
    setDownloading(true);
    try {
      const response = await crudApi.getBinary(binaryEndpoint + file.id);
      if (!response.ok) {
        ToastMessages.Error(response.status === 404 ? "The file is missing on the server." : "Download failed.");
        return;
      }
      // response.data is already a Blob (binaryClient uses responseType: "blob")
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_name;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  if (!file) return null;

  const toggle = selectable && typeof onToggleSelect === "function" ? onToggleSelect : null;
  const cardClick = toggle ?? (showFile ? () => showFile(file) : undefined);
  const borderColor = selected ? "#038edc" : added ? "#51d28c" : "#eaecf0";

  return (
    <Col md={4} className="mb-3">
      <div
        ref={ref}
        draggable={typeof moveFile === "function"}
        onDragStart={(e) => {
          const ids = Array.isArray(dragIds) && dragIds.length > 0 ? dragIds : [file.id];
          e.dataTransfer.setData(FILE_DRAG_TYPE, ids.join(","));
          e.dataTransfer.effectAllowed = "move";
          setDragging(true);
        }}
        onDragEnd={() => setDragging(false)}
        style={{ opacity: dragging ? 0.5 : 1, cursor: typeof moveFile === "function" ? "grab" : undefined }}
      >
        <Card
          onClick={cardClick}
          role={toggle ? "checkbox" : undefined}
          aria-checked={toggle ? selected : undefined}
          style={{
            border: `1.5px solid ${borderColor}`,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: selected ? "0 0 0 3px rgba(3,142,220,0.18)" : "0 2px 8px rgba(0,0,0,0.05)",
            transition: "border-color 0.18s ease, box-shadow 0.18s ease",
            cursor: cardClick ? "pointer" : "default",
            position: "relative",
          }}
          onMouseEnter={showFile && !toggle ? (e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(3,142,220,0.12)") : undefined}
          onMouseLeave={showFile && !toggle ? (e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)") : undefined}
        >
          {toggle && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute", top: 10, left: 10, zIndex: 2,
                width: 24, height: 24, borderRadius: 6,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: selected ? "#038edc" : "rgba(255,255,255,0.92)",
                border: `2px solid ${selected ? "#038edc" : "#c7ccd4"}`,
                color: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              }}
            >
              {selected && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 13 }} />}
            </span>
          )}
          {/* Thumbnail */}
          <div
            style={{
              height: THUMB_HEIGHT,
              overflow: "hidden",
              background: "#f8f9fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {inView && previewFile ? (
              <Thumbnail previewFile={previewFile} />
            ) : inView ? (
              <div className="skeleton" style={{ width: "100%", height: THUMB_HEIGHT }} />
            ) : (
              <FileTypeIcon type={file.mime_type} />
            )}
          </div>

          {/* Info */}
          <div style={{ padding: "12px 14px 8px" }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                marginBottom: 3,
              }}
              title={file.original_name}
            >
              {file.original_name}
            </div>
            <div style={{ fontSize: 12, color: "#9299a8" }}>{formatDate(file.date)}</div>
          </div>

          {/* Actions (hidden while selecting: the batch bar has them) */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: "8px 14px 12px",
              display: toggle ? "none" : "flex",
              gap: 8,
              flexWrap: "wrap",
              borderTop: "1px solid #f3f4f6",
            }}
          >
            {showFile && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showFile(file); }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e5ea",
                  background: "#fff", fontSize: 12, fontWeight: 600, color: "#374151",
                  cursor: "pointer", transition: "border-color 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#038edc")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e5ea")}
              >
                <FontAwesomeIcon icon={faEye} style={{ color: "#038edc" }} />
                {t("Backend.Files.Buttons.Preview") || "Preview"}
              </button>
            )}

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); downloadFile(); }}
              disabled={downloading}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e5ea",
                background: "#fff", fontSize: 12, fontWeight: 600, color: "#374151",
                cursor: downloading ? "default" : "pointer",
                opacity: downloading ? 0.6 : 1,
                transition: "border-color 0.15s ease",
              }}
              onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.borderColor = "#564ab1"; }}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e5ea")}
            >
              <FontAwesomeIcon icon={faDownload} style={{ color: "#564ab1" }} />
              {t("Backend.Buttons.Download")}
            </button>

            {typeof handleClick === "function" && (
              <button
                type="button"
                disabled={added}
                onClick={() => handleClick(file.id, added, setAdded, connectionId, setConnectionId)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8, border: `1.5px solid ${added ? "#e2e5ea" : "#e2e5ea"}`,
                  background: added ? "#f8f9fa" : "#fff", fontSize: 12, fontWeight: 600,
                  color: added ? "#9299a8" : "#51d28c",
                  cursor: added ? "default" : "pointer",
                  opacity: added ? 0.6 : 1,
                }}
              >
                <FontAwesomeIcon icon={added ? faMinus : faPlus} />
                {added
                  ? t("Backend.Applications.Buttons.RemoveFileFromApplication")
                  : t("Backend.Applications.Buttons.AddFileToApplication")}
              </button>
            )}

            {showAdd && typeof handleFileFunction === "function" && (
              <button
                type="button"
                disabled={used}
                onClick={() => { handleFileFunction(file); setUsed(true); }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e5ea",
                  background: used ? "#f8f9fa" : "#fff", fontSize: 12, fontWeight: 600,
                  color: used ? "#9299a8" : "#51d28c",
                  cursor: used ? "default" : "pointer",
                  opacity: used ? 0.6 : 1,
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                {used ? t("Backend.Files.Buttons.Added") || "Added" : t("Backend.Dropdown.Use")}
              </button>
            )}

            {typeof moveFile === "function" && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); moveFile(file); }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e2e5ea",
                  background: "#fff", fontSize: 12, fontWeight: 600, color: "#374151",
                  cursor: "pointer", transition: "border-color 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#f0a500")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e2e5ea")}
              >
                <FontAwesomeIcon icon={faFolderOpen} style={{ color: "#f0a500" }} />
                {t("Backend.Files.Folders.Move", { defaultValue: "Move" })}
              </button>
            )}

            {typeof extraActions === "function" && extraActions(file)}

            {showDelete && typeof deleteFile === "function" && (
              <button
                type="button"
                onClick={() => deleteFile()}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 8, border: "1.5px solid #fde8e8",
                  background: "#fff", fontSize: 12, fontWeight: 600, color: "#f34e4e",
                  cursor: "pointer",
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
                {t("Backend.Files.Buttons.DeleteFile")}
              </button>
            )}
          </div>
        </Card>
      </div>
    </Col>
  );
};

export default FileListGridItem;
